import { getDefinitionReadiness } from '../../utils/definition-readiness';

export type WorkflowSectionKey =
  | 'define'
  | 'design'
  | 'db-design'
  | 'tickets'
  | 'marketplace'
  | 'users'
  | 'contact';

export type WorkflowSectionStatus = {
  key: WorkflowSectionKey;
  title: string;
  hasData: boolean;
  ready: boolean;
  issues: string[];
};

export type DefineSummary = {
  readiness: ReturnType<typeof getDefinitionReadiness>;
};

export type DbDesignSummary = {
  hasSchema: boolean;
  tables: number;
  relationships: number;
  fields: { pk: number; fk: number; other: number };
  topTables: Array<{ name: string; fields: number }>;
};

export type TicketsSummary = {
  hasBoard: boolean;
  totalCards: number;
  byColumn: Array<{ title: string; count: number }>;
  doneCards: number;
  notDoneCards: number;
};

function titleFor(key: WorkflowSectionKey): string {
  switch (key) {
    case 'define': return 'Define';
    case 'design': return 'Design';
    case 'db-design': return 'DB Design';
    case 'tickets': return 'Tickets';
    case 'marketplace': return 'Marketplace';
    case 'users': return 'User Management';
    case 'contact': return 'Contact';
  }
}

export function computeDefineSummary(defineData: any): DefineSummary {
  return { readiness: getDefinitionReadiness(defineData ?? null) };
}

export function computeDbDesignSummary(dbDesignMainData: any): DbDesignSummary {
  const schema = (dbDesignMainData as any)?.schema ?? null;
  const tables = Array.isArray(schema?.tables) ? schema.tables : [];
  const relationships = Array.isArray(schema?.relationships) ? schema.relationships : [];

  let pk = 0;
  let fk = 0;
  let other = 0;
  const topTables = tables
    .map((t: any) => {
      const name = String(t?.name ?? '').trim();
      const fields = Array.isArray(t?.fields) ? t.fields : [];
      for (const f of fields) {
        if (f?.pk) pk++;
        else if (f?.fk) fk++;
        else other++;
      }
      return { name, fields: fields.length };
    })
    .filter((t: any) => Boolean(t.name))
    .sort((a: any, b: any) => b.fields - a.fields)
    .slice(0, 8);

  const hasSchema = tables.length > 0;
  return {
    hasSchema,
    tables: tables.length,
    relationships: relationships.length,
    fields: { pk, fk, other },
    topTables,
  };
}

export function computeTicketsSummary(board: any[] | null): TicketsSummary {
  const cols = Array.isArray(board) ? board : [];
  const byColumn = cols.map((c: any) => ({
    title: String(c?.title ?? '').trim() || 'Untitled',
    count: Array.isArray(c?.cards) ? c.cards.length : 0,
  }));
  const totalCards = byColumn.reduce((sum, c) => sum + c.count, 0);
  const done = byColumn
    .filter((c) => /done|complete|closed/i.test(c.title))
    .reduce((sum, c) => sum + c.count, 0);
  return {
    hasBoard: totalCards > 0 || byColumn.length > 0,
    totalCards,
    byColumn,
    doneCards: done,
    notDoneCards: Math.max(0, totalCards - done),
  };
}

export function computeStatuses(input: {
  projectId: string | null;
  defineData: any;
  dbDesignMainData: any;
  marketplaceSeed: any;
  board: any[] | null;
}): WorkflowSectionStatus[] {
  const { projectId, defineData, dbDesignMainData, marketplaceSeed, board } = input;
  if (!projectId) {
    return (['define', 'design', 'db-design', 'tickets', 'marketplace', 'users', 'contact'] as WorkflowSectionKey[]).map(
      (key) => ({ key, title: titleFor(key), hasData: false, ready: false, issues: ['No project selected'] })
    );
  }

  const defineReadiness = computeDefineSummary(defineData).readiness;
  const defineReady = defineReadiness.ready;

  const defineIssues: string[] = [];
  if (!defineReady) {
    for (const m of (defineReadiness.missing ?? []).slice(0, 4)) defineIssues.push(m);
    if ((defineReadiness.missing?.length ?? 0) > 4) defineIssues.push('…more items missing');
  }

  const designIssues: string[] = [];
  if (!defineReady) designIssues.push('Define is not complete (needs name + description)');

  const dbIssues: string[] = [];
  if (!defineReady) dbIssues.push('Define is not complete (needs name + description)');
  const db = computeDbDesignSummary(dbDesignMainData);
  const dbHasData = db.hasSchema;
  if (!dbHasData) dbIssues.push('No DB schema saved yet');

  const ticketsIssues: string[] = [];
  if (!defineReady) ticketsIssues.push('Define should be complete before tickets');
  const ticketsCols = board;
  const ticketsHasData = Array.isArray(ticketsCols) ? ticketsCols.length > 0 : false;
  if (!ticketsHasData) ticketsIssues.push('No saved ticket board yet');

  const marketplaceIssues: string[] = [];
  if (!defineReady) marketplaceIssues.push('Define incomplete: marketplace listings may lack context');

  return [
    { key: 'define', title: titleFor('define'), hasData: Boolean(defineData), ready: defineReady, issues: defineIssues },
    { key: 'design', title: titleFor('design'), hasData: false, ready: defineReady, issues: designIssues },
    { key: 'db-design', title: titleFor('db-design'), hasData: dbHasData, ready: defineReady && dbHasData, issues: dbIssues },
    { key: 'tickets', title: titleFor('tickets'), hasData: ticketsHasData, ready: defineReady && ticketsHasData, issues: ticketsIssues },
    { key: 'marketplace', title: titleFor('marketplace'), hasData: Boolean(marketplaceSeed), ready: true, issues: marketplaceIssues },
    { key: 'users', title: titleFor('users'), hasData: true, ready: true, issues: [] },
    { key: 'contact', title: titleFor('contact'), hasData: true, ready: true, issues: [] },
  ];
}
